/**
 * Fetches profile shortcuts from the dedicated admin endpoint
 * (`GET /admin-panel-v2/configurations/profile-shortcuts`).
 *
 * Staff-authenticated via `getPrivateInstance` — same pattern as other
 * admin-panel-v2 configuration reads (e.g. maintenance mode). Returns
 * `null` (rather than throwing) when the request fails so
 * `useProfileShortcuts` can fall back to an empty list —
 * `ShortcutsTable` already renders an empty-state message for `[]` —
 * instead of crashing the page. Callers should gate that empty state on
 * `isLoading` so it does not flash while the GET is in flight.
 */
import { API_ROUTES } from "../../api/ApiRoutes";
import type { ProfileShortcutDto } from "../../types/profileShortcuts";
import { getPrivateInstance } from "../_privateInstance";

type ProfileShortcutsEnvelope = {
  success?: boolean;
  data?: ProfileShortcutDto[] | { shortcuts?: ProfileShortcutDto[] };
  shortcuts?: ProfileShortcutDto[];
};

/**
 * Unwraps the list from either `{ data: [...] }`, `{ data: { shortcuts } }`,
 * or a top-level `{ shortcuts }` envelope so a gateway wrap change doesn't
 * silently empty the table.
 */
export const extractProfileShortcutsDto = (
  body: ProfileShortcutsEnvelope | null | undefined,
): ProfileShortcutDto[] | null => {
  if (!body) return null;
  if (Array.isArray(body.data)) return body.data;
  if (Array.isArray(body.data?.shortcuts)) return body.data.shortcuts;
  if (Array.isArray(body.shortcuts)) return body.shortcuts;
  return null;
};

export const fetchProfileShortcutsDto = async (): Promise<
  ProfileShortcutDto[] | null
> => {
  try {
    const { data: body } =
      await getPrivateInstance().get<ProfileShortcutsEnvelope>(
        API_ROUTES.GET_PROFILE_SHORTCUTS,
      );
    if (body?.success === false) return null;
    return extractProfileShortcutsDto(body);
  } catch {
    return null;
  }
};
